import { createSlice } from "@reduxjs/toolkit";
import {
    GetAllAssetsList,
    GetAssetByIDThunk,
    updateExistingAssets,
    assignAsset,
    deleteExistingAsset,
    initiateAssetTransfer,
    confirmAssetTransfer,
    rejectAssetTransfer,
    getAssetTransferHistory,
    getAssetHistory,
    getPendingTransfers,
    getMyTransfers,
    deletePendingTransfer
} from './thunk';

export const initialState = {
    assetList: [],
    assetHistory: [],
    transferHistory: [],
    pendingTransfers: [],
    myTransfers: [],
    errors: {}
};

const DesignationSlice = createSlice({
    name: 'Assets',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(GetAllAssetsList.fulfilled, (state: any, action: any) => {
            state.assetList = action.payload?.data || action.payload;
        });
        builder.addCase(GetAllAssetsList.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || null;
        });
        
        // Get Asset By ID
        builder.addCase(GetAssetByIDThunk.fulfilled, (state: any, action: any) => {
            const assetData = action.payload?.data || action.payload;
            // Add to list if not already present
            const existingIndex = state.assetList.findIndex((asset: any) => asset.id === assetData.id);
            if (existingIndex === -1) {
                state.assetList.push(assetData);
            } else {
                state.assetList[existingIndex] = assetData;
            }
        });
        builder.addCase(GetAssetByIDThunk.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || null;
        });
        
        // Update Asset
        builder.addCase(updateExistingAssets.fulfilled, (state: any, action: any) => {
            const updatedAsset = action.payload?.data || action.payload;
            if (updatedAsset && updatedAsset.id) {
                const existingIndex = state.assetList.findIndex((asset: any) => asset.id === updatedAsset.id);
                if (existingIndex !== -1) {
                    state.assetList[existingIndex] = updatedAsset;
                } else {
                    state.assetList.push(updatedAsset);
                }
            }
        });
        builder.addCase(updateExistingAssets.rejected, (state: any, action: any) => {
            state.error = action.payload?.error || null;
        });
        
        // Assign Asset
        builder.addCase(assignAsset.fulfilled, (state: any, action: any) => {
            const assignmentData = action.payload?.assignmentData || action.meta.arg;
            
            // Check if this is an unassign operation
            if (assignmentData && assignmentData.assigned_to === null) {
                // Handle unassign operation
                const assetId = assignmentData.id;
                const index = state.assetList.findIndex((asset: any) => asset.id === assetId);
                if (index !== -1) {
                    console.log('Unassigning asset in Redux:', {
                        assetId,
                        oldStatus: state.assetList[index].asset_status,
                        oldAssignedTo: state.assetList[index].assigned_to
                    });
                    
                    state.assetList[index] = {
                        ...state.assetList[index],
                        assigned_to: null,
                        assignment_date: null,
                        unassign_reason: assignmentData.unassign_reason,
                        unassigned_date: assignmentData.unassigned_date,
                        asset_status: 'Available'
                    };
                    
                    console.log('Asset unassigned in Redux:', {
                        newStatus: state.assetList[index].asset_status,
                        newAssignedTo: state.assetList[index].assigned_to
                    });
                } else {
                    console.log('Asset not found in Redux for unassign:', assetId);
                }
            } else {
                // Handle assign operation
                // If we have updated asset data, use that
                if (action.payload?.updatedAsset) {
                    const updatedAsset = action.payload.updatedAsset;
                    const index = state.assetList.findIndex((asset: any) => asset.id === updatedAsset.id);
                    if (index !== -1) {
                        state.assetList[index] = updatedAsset;
                    } else {
                        state.assetList.push(updatedAsset);
                    }
                } else if (action.payload?.data?.id) {
                    // API returned just the ID, so we need to update the asset in the list
                    const assetId = action.payload.data.id;
                    const index = state.assetList.findIndex((asset: any) => asset.id === assetId);
                    if (index !== -1) {
                        // Update the existing asset with assignment info
                        state.assetList[index] = {
                            ...state.assetList[index],
                            assigned_to: assignmentData.assigned_to,
                            assignment_date: assignmentData.assignment_date,
                            asset_status: 'Assigned'
                        };
                    }
                } else if (action.payload?.data) {
                    // Full asset data returned
                    const index = state.assetList.findIndex((asset: any) => asset.id === action.payload.data.id);
                    if (index !== -1) {
                        state.assetList[index] = action.payload.data;
                    } else {
                        state.assetList.push(action.payload.data);
                    }
                } else if (action.payload) {
                    // Handle case where response is directly the data
                    const index = state.assetList.findIndex((asset: any) => asset.id === action.payload.id);
                    if (index !== -1) {
                        state.assetList[index] = action.payload;
                    } else {
                        state.assetList.push(action.payload);
                    }
                }
            }
        });
        
        // Delete Asset
        builder.addCase(deleteExistingAsset.fulfilled, (state: any, action: any) => {
            console.log('Delete asset fulfilled in reducer:', action.meta.arg.id);
            
            // Remove the asset from the list
            const originalLength = state.assetList.length;
            state.assetList = state.assetList.filter((asset: any) => asset.id !== action.meta.arg.id);
            console.log(`Asset list updated: ${originalLength} -> ${state.assetList.length}`);
            
            // Clear all related history data for this asset
            const deletedAssetId = action.meta.arg.id;
            
            // Clear asset history if it exists and contains data for this asset
            if (state.assetHistory && Array.isArray(state.assetHistory)) {
                state.assetHistory = state.assetHistory.filter((history: any) => 
                    history.asset_id !== deletedAssetId
                );
            }
            
            // Clear transfer history if it exists and contains data for this asset
            if (state.transferHistory && Array.isArray(state.transferHistory)) {
                state.transferHistory = state.transferHistory.filter((transfer: any) => 
                    transfer.asset_id !== deletedAssetId
                );
            }
            
            // Clear pending transfers if they exist and contain data for this asset
            if (state.pendingTransfers && Array.isArray(state.pendingTransfers)) {
                state.pendingTransfers = state.pendingTransfers.filter((transfer: any) => 
                    transfer.asset_id !== deletedAssetId
                );
            }
            
            // Clear my transfers if they exist and contain data for this asset
            if (state.myTransfers && Array.isArray(state.myTransfers)) {
                state.myTransfers = state.myTransfers.filter((transfer: any) => 
                    transfer.asset_id !== deletedAssetId
                );
            }
        });

        builder.addCase(deleteExistingAsset.rejected, (state: any, action: any) => {
            console.error('Delete asset rejected in reducer:', action.error);
            console.error('Rejection details:', {
                error: action.error,
                payload: action.payload,
                meta: action.meta
            });
        });

        // Asset Transfer Cases
        builder.addCase(initiateAssetTransfer.fulfilled, (state: any, action: any) => {
            // Update asset status if transfer is immediate
            if (action.payload?.asset_id) {
                const assetIndex = state.assetList.findIndex((asset: any) => asset.id === action.payload.asset_id);
                if (assetIndex !== -1) {
                    state.assetList[assetIndex].asset_status = 'Transfer Pending';
                }
            }
        });

        builder.addCase(confirmAssetTransfer.fulfilled, (state: any, action: any) => {
            // Update asset assignment and status
            if (action.payload?.asset_id) {
                const assetIndex = state.assetList.findIndex((asset: any) => asset.id === action.payload.asset_id);
                if (assetIndex !== -1) {
                    state.assetList[assetIndex].employee_id = action.payload.to_employee_id;
                    state.assetList[assetIndex].employee_name = action.payload.to_employee_name;
                    state.assetList[assetIndex].asset_status = 'Assigned';
                    state.assetList[assetIndex].assigned_date = new Date().toISOString();
                }
            }
        });

        builder.addCase(rejectAssetTransfer.fulfilled, (state: any, action: any) => {
            // Revert asset status if transfer was rejected
            if (action.payload?.asset_id) {
                const assetIndex = state.assetList.findIndex((asset: any) => asset.id === action.payload.asset_id);
                if (assetIndex !== -1) {
                    state.assetList[assetIndex].asset_status = 'Assigned';
                }
            }
        });

        // Asset History Cases
        builder.addCase(getAssetHistory.fulfilled, (state: any, action: any) => {
            state.assetHistory = action.payload?.data || action.payload || [];
        });

        builder.addCase(getAssetTransferHistory.fulfilled, (state: any, action: any) => {
            state.transferHistory = action.payload?.data || action.payload || [];
        });

        builder.addCase(getPendingTransfers.fulfilled, (state: any, action: any) => {
            state.pendingTransfers = action.payload?.data || action.payload || [];
        });

        builder.addCase(getMyTransfers.fulfilled, (state: any, action: any) => {
            state.myTransfers = action.payload?.data || action.payload || [];
        });

        // Delete pending transfer
        builder.addCase(deletePendingTransfer.fulfilled, (state: any, action: any) => {
            const transferId = action.payload?.transferId || action.meta.arg;
            if (state.pendingTransfers && Array.isArray(state.pendingTransfers)) {
                state.pendingTransfers = state.pendingTransfers.filter((t: any) => t.id !== transferId && t.transfer_id !== transferId);
            }
            if (state.transferHistory && Array.isArray(state.transferHistory)) {
                state.transferHistory = state.transferHistory.filter((t: any) => t.id !== transferId && t.transfer_id !== transferId);
            }
        });
    }
});

export default DesignationSlice.reducer;